import { FC } from "react";
import TableComponent, {
  TableProps,
} from "../../../../common/components/table/table.component";
import RouteActiveListingTableColumn from "../common/types/route-active-listing-table-column";
import routeActiveListingTableHelper from "./route-active-listing-table.helper";
import RouteActiveListingTableRow from "../common/types/route-active-listing-table-row";
import useRouteActiveListingUserPermissions from "../common/user-permissions/use-route-active-listing-user-permissions";
import dateService from "../../../../common/utils/date/date.service";
import { PlanEntryActiveListItem } from "../../../../common/services/plan-entry/active-list/plan-entry-active-list";
import RouteDetailsComponent from "../../details/route-details.component";

type RouteActiveListingTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  expandedRowsUuids: string[];
  listingItems: PlanEntryActiveListItem[];
  onExpandedRowsUuidsChange: (expandedRowsUuids: string[]) => void;
};

const RouteActiveListingTableComponent: FC<RouteActiveListingTableProps> = (
  props,
) => {
  const userPermissions = useRouteActiveListingUserPermissions();

  const expandRow = (routeUuid: string) => {
    props.onExpandedRowsUuidsChange([...props.expandedRowsUuids, routeUuid]);
  };

  const collapseRow = (routeUuid: string) => {
    const filteredExpandedRows = props.expandedRowsUuids.filter(
      (expandedRowUuid) => expandedRowUuid !== routeUuid,
    );

    props.onExpandedRowsUuidsChange(filteredExpandedRows);
  };

  const createTableRow = (
    listingItem: PlanEntryActiveListItem,
  ): RouteActiveListingTableRow => {
    const isRowExpanded = props.expandedRowsUuids.includes(listingItem.uuid);

    const onRowClick = () => {
      if (isRowExpanded) {
        collapseRow(listingItem.uuid);
        return;
      }

      expandRow(listingItem.uuid);
    };

    const startDate = dateService.formatDateTime(listingItem.startTime);
    const cargoOrderHumanIds = listingItem.cargoOrderHumanIds.join(", ");

    return {
      id: listingItem.uuid,
      idForTesting: listingItem.uuid,
      onClick: onRowClick,
      hiddenComponent: (
        <RouteDetailsComponent
          routeUuid={listingItem.uuid}
          routeHumanId={listingItem.humanId}
          isVisible={isRowExpanded}
        />
      ),
      isHiddenComponentVisible: isRowExpanded,
      value: {
        startDate: <div title={startDate}>{startDate}</div>,
        humanId: (
          <div title={String(listingItem.humanId)}>{listingItem.humanId}</div>
        ),
        cargoOrderHumanIds: (
          <div title={cargoOrderHumanIds}>{cargoOrderHumanIds}</div>
        ),
        route: (
          <div>
            {listingItem.routeAddresses.map((routeAddress, index) => {
              const stageNo = index + 1;
              return (
                <div
                  title={routeAddress}
                  key={index}
                >{`${stageNo}. ${routeAddress}`}</div>
              );
            })}
          </div>
        ),
        passengers: (
          <div>
            {listingItem.passengers.map((passenger, index) => (
              <div title={passenger} key={index}>
                {passenger}
              </div>
            ))}
          </div>
        ),
        ...(userPermissions.hasAccessToTaxiCorporation && {
          taxiCorporation: listingItem.taxiCorporationName,
        }),
        driver: (
          <div title={listingItem.driverName}>{listingItem.driverName}</div>
        ),
      },
    };
  };

  const columns: RouteActiveListingTableColumn[] =
    routeActiveListingTableHelper.getColumns(userPermissions);

  const rows: RouteActiveListingTableRow[] = props.listingItems.map((item) =>
    createTableRow(item),
  );

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isError={props.isError}
      isLoading={props.isLoading}
    />
  );
};

export default RouteActiveListingTableComponent;
